import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchData, normilizeFilename } from "../utils";
import * as endpoints from '../settings';
import { editNotification, removeNotification, setNotification } from "./notificationsSlice";
import sortBy from "lodash.sortby";
import { decamelize } from "humps";
import { schemas } from "./schemas";
import { filter, find, min, omit, size } from "lodash";
import keycloak from "../Keycloak";

export const uploadContracts = createAsyncThunk(
    "uploadContracts",
    async(file, { dispatch, rejectWithValue }) => {
        try {
            const fmData = new FormData();
            fmData.append("file", file);
            const response = await fetchData(endpoints.API_UPLOAD_CONTRACTS, {
                method: "put",
                body: fmData
            }, schemas.UPLOAD_CONTRACTS);
            dispatch(setNotification({
                message: "Contracts uploaded successfully",
                success: true,
                closable: false,
                log: Object.values(response.entities.contracts)
            }));
            return { entity: "contracts", ...response.result};
        } catch(e) {
            dispatch(setNotification(e));
            return rejectWithValue({});
        }
});


const downloadFile = createAsyncThunk(
    "downloadFile",
    async (uuid, { dispatch }) => {
        try {
            const response = await fetchData(endpoints.API_PACKAGE_DOWNLOAD + "/" + uuid, {}, {});
            let a = document.createElement("a");
            a.download = response.result.saveAs;
            a.href = response.result.url;
            a.rel = "noopener noreferrer";
            a.target = "_blank";
            a.click();
            a.remove();
        } catch(e) {
            dispatch(setNotification(e));
        }
});

export const downloadPackage = createAsyncThunk(
    "downloadPackage",
    async({ key, minos, prereq }, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(endpoints.API_PACKAGE_DOWNLOAD, {
                headers: {
                    "X-Pkg-Id": key,
                    "X-Os-Version": minos
                }
            }, {});
            let urls = response.result.urls;
            if (prereq) {
                sortBy(urls, ["order"]).map(url => {
                    dispatch(downloadFile(url.url));
                })
            } else {
                let url = sortBy(urls, ["order"]).pop();
                dispatch(downloadFile(url.url));
            }
            return key;
        } catch(e) {
            dispatch(setNotification(e));
        }
        return rejectWithValue(false);
    }
);

/**
 * Three phases:
 *  1. check input fields: try to insert record immediately.
 *  2. Start upload and md5 calculation at the same time. Progress shows minimum of them.
 *  2.a When packages tab is showed, refresh view.
 *  2.b TODO Delete package record on any error adn 2.a.
 *  3. Save md5 value to db.
 */
export const registerPackage = createAsyncThunk(
    "registerPackage",
    async ({ data, files=[], edit }, { dispatch, rejectWithValue, signal, getState }) => {
        try {
            const fmData = new FormData();
            if (Array.isArray(files) && files.length) {
                // calculate md5 before sending the request
                let notification = dispatch(setNotification({ message: "Calculating MD5 checksum for files...", closable: false })), notificationId;
                if (notification && notification.payload && notification.payload.id) {
                    notificationId = notification.payload.id;
                };
                let filesData = await Promise.all(files.map(async({ file, md5 }, idx) => ({
                        name: file.name,
                        size: file.size,
                        last_modified: file.lastModified,
                        save_as: file.name,
                        registered_md5: md5 || undefined,
                        calculated_md5: await calcMd5({
                            file: file,
                            id: idx,
                            notificationId: notificationId,
                            pkg: data.name,
                            dispatch,
                            signal,
                            getState
                        })
                    }))
                );
                dispatch(removeNotification(notificationId));
                fmData.append("files", JSON.stringify(filesData));
            }
            fmData.append("data", JSON.stringify(data));
            const response = await fetchData(edit ? endpoints.API_PACKAGE_UPDATE : endpoints.API_PACKAGE_REGISTER, {
                method: "put",
                body: fmData
            }, {});

            if (size(response.result)) {
                dispatch(setNotification({
                    success: true,
                    message: `The ${data.name} successfully ${edit ? "updated" : "registered"}.`,
                    link: !edit && { href: `packages/${response.result.packageNumber}`, title: "View the package"},
                    closable: edit
                }));
                if (Array.isArray(files) && files.length) {
                    Promise.all(Object.values(response.result.files).map(async res => {
                        let file = find(files, file => (normilizeFilename(file.file.name)) == res.fields.key.split("/")[1]);
                        if (file) {
                            const id = res.packageFileNumber;
                            dispatch(setUploadProgress({id: id, progress: 0}));
                            await uploadPackage({
                                id: id,
                                file: file.file,
                                fields: res.fields,
                                url: res.url,
                                dispatch,
                                signal
                            });
                            return await dispatch(setFileUploaded(id));
                        } else {
                            throw new Error("File not found")
                        };
                    }));
                }
            }
            return { entity: "packages" };
        } catch(e) {
            dispatch(setNotification(e));
        };
        return rejectWithValue({});
});

const uploadPackage = ({ id, file, fields, url, headers, dispatch }) => {
    return new Promise((resolve, reject) => {

        const xhr = new XMLHttpRequest();

        const fmData = new FormData();
        Object.keys(fields).forEach(key => {
            fmData.append(decamelize(key, { separator: "-" }), fields[key]);
        });
        fmData.append("file", file);

        if (xhr.upload) {
            xhr.upload.onprogress = (e) => {
                let done = e.position || e.loaded, total = e.totalSize || e.total;
                let percent = Math.floor(done / total * 1000) / 10;
                percent == 100
                    ? dispatch(inProcessUploadFile(id))
                    : dispatch(setUploadProgress({ id: id, progress: percent }));
            };
        };

        xhr.onload = function (e) {
            let code = xhr.status;
            if (code >= 200 && code <= 299) {
                dispatch(successUploadFile(id));
                resolve(id);
            } else {
                dispatch(failureUploadFile(id));
                let err = {};
                err.code = code;
                err.type = "error";
                let message = "Unknown error";
                try {
                    let json = JSON.parse(xhr.responseText);
                    if (json && typeof json === 'object' && json !== null) {
                        message = 'reason' in json ? json.reason : ('message' in json ? json.message : 'Unknown error');
                    }
                } catch (e) {
                    message =  xhr.responseText.match("<Message>(.*?)</Message>");
                }
                err.message = Array.isArray(message) ? message[1] : message;
                reject(err);
            }
        };

        xhr.onabort = function () {
            dispatch(cancelUploadFile(id));
        };

        xhr.onerror = () => {
            dispatch(failureUploadFile(id));
        };

        const sendRequest = (data) => {
            xhr.open("post", url, true);
            if (headers) {
                Object.keys(headers).map(header => xhr.setRequestHeader(header, headers[header]));
            }
            xhr.send(data);
        }

        sendRequest(fmData);

        return {
            abort: () => xhr.abort(),
            reload: () => sendRequest(fmData)
        }
    });
}

export const setFileUploaded = createAsyncThunk(
    "setFileUploaded",
    async (id, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(endpoints.API_PACKAGE_FILE_UPLOADED, {
                method: "put",
                headers: { "X-Pkg-File-Id": id }
            }, {});
            dispatch(setNotification({ success: true, message: "File successfully uploaded" }));
            return { entity: "packages" };
        } catch (error) {
            dispatch(setNotification(error));
        };
        return rejectWithValue();
});

const calcMd5 = ({ id, notificationId, pkg, file, dispatch, signal, getState }) => {
    return new Promise((resolve, reject) => {
        signal.addEventListener("abort", () => worker.terminate());
        dispatch(setMd5Progress({ id: id, pkg: pkg, md5progress: 0 }));

        const worker = new Worker(`${process.env.PUBLIC_URL}/js/md5-worker.js`);
        worker.postMessage({ file });
        worker.onerror = (err) => {
            reject(err);
        };
        worker.onmessage = (e) => {
            const { currentPart, totalParts, hashResult } = e.data;
            let percent = Math.floor(currentPart / (totalParts || 1) * 1000) / 10;
            dispatch(setMd5Progress({ id: id, pkg: pkg, md5progress: percent }));
            const { md5progress } = getState().upload;
            dispatch(editNotification({ id: notificationId, message: "Calculating MD5 checksum for files... " + min(filter(md5progress, { pkg: pkg }).map(u => u.md5progress)) + "%" }))
            if (percent == 100 && hashResult) {
                worker.terminate();
                resolve(hashResult);
            }
        };
    });
};

export const uploadShcFile = createAsyncThunk(
    "uploadShcFile",
    async (file, { dispatch, signal, rejectWithValue }) => {
        try {
            if (file) {
                const id = file.uid;
                await keycloak.updateToken(10);
                const token = keycloak.token;
                dispatch(setUploadProgress({id: id, progress: 0, type: "shc", name: file.name}));
                const response = await uploadPackage({
                    id: id,
                    file: file,
                    fields: {},
                    url: endpoints.SHC_UPLOAD,
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "X-File-Name": file.name
                    },
                    dispatch,
                    signal
                });
                dispatch(setNotification({ success: true, message: file.name + " has been uploaded successfully"}));
                return { entity: "uploads", ...response.result }
            }
        } catch (e) {
            dispatch(setNotification(e));
            return rejectWithValue(e);
        }
    }
);

export const deleteShcFile = createAsyncThunk(
    "deleteShcFile",
    async (file, { dispatch }) => {
        try {
            const response = await fetchData(endpoints.SHC_UPLOAD, {
                method: "DELETE",
                headers: { "X-Upload-Ref": file }
            });
            dispatch(setNotification(response.result));
            return {entity: "shc", ...response.result};
        } catch (e) {
            dispatch(setNotification(e));
        }
    }
);

const upload = createSlice({
    name: 'upload',
    initialState: {
        uploads: {},
        md5progress: []
        // format of items will be like below
        // {
        //   id: [id],
        //   name: filename,
        //   progress: progress,
        //   status: 0 - sucessfully uploaded, -1 - failed, 1 - cancelled, 3 - in progress
        //   type: [shc, t4]
        // }
    },
    reducers: {
        setUploadProgress: (state, action) => {
            if (state.uploads[action.payload.id]) {
                // update only the progress
                state.uploads[action.payload.id] = {
                    ...state.uploads[action.payload.id],
                    progress: action.payload.progress
                }
            } else {
                state.uploads[action.payload.id] = {
                    ...omit(action.payload, ["id"]),
                    status: 3
                }
            }
        },
        setMd5Progress: (state, action) => {
            let upload = find(state.md5progress, { id: action.payload.id, pkg: action.payload.pkg });
            if (upload) {
                upload.md5progress = action.payload.md5progress;
            } else {
                state.md5progress.push({
                    id: action.payload.id,
                    md5progress: action.payload.md5progress,
                    pkg: action.payload.pkg
                })
            };
        },
        inProcessUploadFile: (state, action) => {
            state.uploads[action.payload] = {
                ...state.uploads[action.payload],
                status: 3
            }
        },
        successUploadFile: (state, action) => {
            let upload = state.uploads[action.payload];
            if (upload) {
                if (upload.type) {
                    state.uploads[action.payload] = { ...state.uploads[action.payload], progress: 100, status: 0 }
                } else {
                    delete state.uploads[action.payload];
                }
            }
        },
        failureUploadFile: (state, action) => {
            state.uploads[action.payload] = {
                ...state.uploads[action.payload],
                status: -1,
                progress: 0
            }
        },
        cancelUploadFile: (state, action) => {
            state.uploads[action.payload] = {
                ...state.uploads[action.payload],
                status: 1
            }
        },
        clearUploads: (state, action) => {
            state.uploads = filter(state.uploads, up => up.status === 3);
        }
    },
    extraReducers: {
        [downloadPackage.pending]: () => {
            document.body.style.cursor = "progress";
        },
        [downloadPackage.rejected]: () => {
            document.body.style.cursor = "default";
        },
        [downloadPackage.fulfilled]: () => {
            document.body.style.cursor = "default";
        },
        [downloadPackage.rejected]: () => {
            document.body.style.cursor = "default";
        }
    }
});

export const { setUploadProgress, setMd5Progress, inProcessUploadFile, successUploadFile, failureUploadFile, cancelUploadFile, clearUploads } = upload.actions;
export default upload.reducer;